import { channel } from 'redux-saga';
import {
  all,
  call,
  delay,
  race,
  select,
  fork,
  put,
  take,
  takeLatest,
  takeEvery,
} from 'typed-redux-saga/macro';
import { redirect } from 'redux-first-router';

import { sagaIntl } from '@i18n';

import { enqueueSnackbar } from '../snackbar';
import { ROUTE_MEDIA_DETAILS } from '../routing';
import {
  redactFileCompleted,
  REDACTING_FILE,
  SET_REDACT_JOB_STARTING,
} from './index';
import {
  REDACT_TDO,
  REDACTED_FILE_COMPLETED,
  SELECT_REDACTED_FILES_TAB,
} from '../mediaDetails/actions';
import { selectCurrentTdoId } from '../mediaDetails/selectors';
import { isImageFile } from '../mediaDetails/saga/imageRedactionSaga';
import { TDOId } from '@common-modules/universal/models/Brands';

export const redactedFilesChannel =
  channel<ReturnType<typeof SELECT_REDACTED_FILES_TAB>>();

// interface Action<P> {
//   type: string;
//   payload: P;
// }

export function* initRedactFile() {
  yield* all([
    fork(watchRedactFileCompleted),
    fork(watchRedactedFilesChannel),
    fork(watchRedactTdoForStartingFlag),
  ]);
}

// Eager spinner: REDACTING_FILE only fires several async hops into the
// redactTDOToWorker saga, so any UI that mirrors "redaction in flight" lags by
// a noticeable beat after the user clicks REDACT FILE. Set a flag immediately
// on REDACT_TDO and let the reducer clear it on REDACTING_FILE; the 10s race
// is a safety net for the case where the worker exits early (e.g. no primary
// asset) and REDACTING_FILE never fires.
export function* watchRedactTdoForStartingFlag() {
  yield* takeLatest(REDACT_TDO, function* () {
    yield* put(SET_REDACT_JOB_STARTING(true));
    yield* race({
      redactingFile: take(REDACTING_FILE),
      timeout: delay(10000),
    });
    yield* put(SET_REDACT_JOB_STARTING(false));
  });
}

export const handelSelectRedactedFilesTab = (tdoId: TDOId) => () =>
  redactedFilesChannel.put(SELECT_REDACTED_FILES_TAB({ tdoId }));

export function* redactFileCompletedHandling(action: {
  payload: { tdoId: TDOId; documentName?: string; quiet?: boolean };
}) {
  const { tdoId, documentName, quiet } = action.payload;
  if (quiet) {
    // One of several files in a bulk run: the caller reports on the set. Still
    // releases the per-file lock, which is the half of this that is not about
    // telling anybody anything.
    yield* put(redactFileCompleted(tdoId));
    return;
  }
  const currentTdoId = yield* select(selectCurrentTdoId);
  const isCurrentTdo = tdoId === currentTdoId;
  const isImage = isCurrentTdo && Boolean(yield* call(isImageFile));

  const intl = sagaIntl();
  const message = isCurrentTdo
    ? intl.formatMessage({
        id: 'successfulRedact',
        defaultMessage: 'File redacted successfully.',
      })
    : documentName
      ? // Named whenever the caller knows the name. This is the branch a
        // reviewer sees after leaving the editor with a burn-in running, and
        // a TDO id is the least useful handle they have on a file they named
        // themselves.
        intl.formatMessage(
          {
            id: 'successfulRedactOtherFileNamed',
            defaultMessage: '"{fileName}" redacted successfully.',
          },
          { fileName: documentName }
        )
      : intl.formatMessage(
          {
            id: 'successfulRedactOtherFile',
            defaultMessage: 'File redacted successfully. (File ID: {fileId})',
          },
          { fileId: tdoId }
        );

  yield* put(
    enqueueSnackbar(
      isImage || !isCurrentTdo
        ? { message, variant: 'success' }
        : {
            message,
            variant: 'success',
            action: 'VIEW FILE',
            handleAction: handelSelectRedactedFilesTab(tdoId),
          }
    )
  );
  yield* put(redactFileCompleted(tdoId));
}

export function* watchRedactFileCompleted() {
  yield* takeEvery(REDACTED_FILE_COMPLETED, redactFileCompletedHandling);
}

// handle `handleAction` of snack bar when click to View File
export function* watchRedactedFilesChannel() {
  while (true) {
    const action = yield* take(redactedFilesChannel);
    const tdoId = yield* select(selectCurrentTdoId);
    if (action.payload.tdoId !== tdoId) {
      yield* put(
        redirect(ROUTE_MEDIA_DETAILS({ tdoId: action.payload.tdoId }))
      );
    }
    yield* put(action);
  }
}
